import React, { useEffect, useState } from "react";
import styled from "styled-components/macro";
import { DateRange } from "../InputCalendar";
import { useCalendar } from "../Calendar";
import { useTranslation, Trans } from "react-i18next";
import { DefaultFont } from "../../styles/fonts";
import { DateNone, IHouse, IMatch, IPerson } from "../../interfaces/intarfaces";
import { Button } from "antd";
import { calculateHousePrice } from "../Chat/utils";
import { useSendInvite } from "../../API/queries/hostApiQueries";
import { isoDate } from "../../utils/helpers";
import { generatePath, useNavigate } from "react-router-dom";
import { HOST_ROOM_EDIT_ROUTE, MatchStatusEnum } from "../../utils/constants";
import { SendInviteModal } from "../Chat/SendInviteModal";
import { useNotifier } from "../../context/ContextNotifier";
import { formatDateToShort, parseServerDate } from "../../utils/time";
import { ContainerSelectedValue } from "../MultiSelect/styles";
import {
  useHostCancelInviteWithConfirmation,
  useHostRemoveTenantWithConfirmation
} from "./hooks";

const Root = styled.div`
  background: #f2f2f2;
  padding: 24px;
  border-radius: 8px;

  ${ContainerSelectedValue} {
    border: 1px solid ${(props) => props.theme.colors.border};
  }
`;

const Title = styled.div`
  ${DefaultFont};
  font-size: 18px;
  color: ${(p) => p.theme.colors.chatTextColor};
  margin-bottom: 16px;
`;
const DatesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TotalRentContainer = styled.div`
  margin-top: 16px;
  font-size: 14px;
  display: flex;
  justify-content: space-between;
`;
const EditRentLink = styled.span`
  color: #a3a3a3;
  font-size: 13px;
  margin-left: 10px;
  cursor: pointer;
`;
const Bold = styled.span`
  font-weight: ${(p) => p.theme.font.weightMedium};
`;
const ButtonsContainer = styled.div`
  margin-top: 16px;
  display: flex;
  gap: 10px;
`;
export const StudentInviteBox: React.FC<{
  host: IPerson;
  student: IPerson;
  house: IHouse;
  match: IMatch;
  initialDates?: { start?: Date; end?: Date };
  additionalInfo: React.ReactNode;
  allowOnlyCancel?: boolean;
}> = ({ host, student, house, match, allowOnlyCancel }) => {
  const [showEditInvite, setShowEditInvite] = useState(false);
  const notifier = useNotifier();

  let startDate: DateNone = undefined;
  let endDate: DateNone = undefined;
  if (match.match_status === MatchStatusEnum.INVITED) {
    startDate = parseServerDate(match.offered_check_in_date!);
    endDate = parseServerDate(match.offered_check_out_date!);
  } else if (
    match.match_status === MatchStatusEnum.ACCEPTED ||
    match.match_status === MatchStatusEnum.PENDING ||
    match.match_status === MatchStatusEnum.ACTIVE ||
    match.match_status === MatchStatusEnum.ARCHIVED
  ) {
    startDate = parseServerDate(match.check_in_date!);
    endDate = parseServerDate(match.check_out_date!);
  }

  const dateIn = {
    ...useCalendar({
      value: startDate
    }),
    required: true
  };
  const dateOut = {
    ...useCalendar({
      value: endDate
    }),
    required: true
  };

  useEffect(() => {
    if (!startDate && !endDate) {
      dateOut.onChange(undefined, undefined);
      dateIn.onChange(undefined, undefined);
    } else {
      // first we set dateOut, then dateIn. otherwise there is an error
      dateOut.onChange(endDate, endDate);
      dateIn.onChange(startDate, startDate);
    }
  }, [startDate?.getTime(), endDate?.getTime()]);

  const [isDateInFirst, setIsDateInFirst] = useState<number>(0);
  const [isAnyDateOut, setIsAnyDateOut] = useState<boolean>(false);
  const { i18n, t } = useTranslation("chats");
  const navigate = useNavigate();
  const rentalError = undefined;

  const sendInvite = useSendInvite();
  const handleCancel = useHostCancelInviteWithConfirmation();
  const handleRemoveTenant = useHostRemoveTenantWithConfirmation();

  const handleSend = () => {
    const payload = {
      host_id: host.user_id!,
      house_id: house.house_id!,
      student_id: student.user_id!,
      student_check_in_date: isoDate(dateIn.value),
      student_check_out_date: isoDate(dateOut.value)
    };
    sendInvite.mutate(payload, {
      onSuccess: () => {
        notifier.success({
          message: t("studentInviteBox.invited")
        });
      }
    });
  };

  if (
    [MatchStatusEnum.CHATTED].includes(match.match_status!) &&
    allowOnlyCancel
  ) {
    return null;
  }

  if (
    ![
      MatchStatusEnum.CHATTED,
      MatchStatusEnum.INVITED,
      MatchStatusEnum.ACCEPTED,
      MatchStatusEnum.PENDING,
      MatchStatusEnum.ACTIVE,
      MatchStatusEnum.ARCHIVED
    ].includes(match.match_status!)
  ) {
    return null;
  }

  const rentPrice = (
    <TotalRentContainer>
      <>
        <Trans
          i18nKey='chats:studentInviteBox.totalRentalPrice'
          components={[<Bold />]}
          values={{ price: calculateHousePrice(house) }}
        />
      </>

      {match.match_status !== MatchStatusEnum.ARCHIVED ? (
        <EditRentLink
          onClick={() => {
            navigate(
              generatePath(HOST_ROOM_EDIT_ROUTE, {
                lng: i18n.language
              }) +
              "/" +
              house.house_id
            );
          }}
        >
          {t("studentInviteBox.editRates")}
        </EditRentLink>
      ) : null}
    </TotalRentContainer>
  );

  const rentDates =
    startDate && endDate ? (
      <div>
        {formatDateToShort(startDate)} - {formatDateToShort(endDate)}
      </div>
    ) : null;

  return (
    <Root>
      <Title>
        {[MatchStatusEnum.CHATTED, MatchStatusEnum.LIKED].includes(
          match.match_status!
        )
          ? t('studentInviteBox.invite', { name: student.first_name })
          : ""}
        {match.match_status === MatchStatusEnum.INVITED
          ? t("studentInviteBox.awaitingConfirmation")
          : ""}
        {match.match_status === MatchStatusEnum.ACCEPTED
          ? t('studentInviteBox.acceptedInvite', { name: student.first_name })
          : ""}
        {[MatchStatusEnum.PENDING, MatchStatusEnum.ACTIVE].includes(
          match.match_status!
        )
          ? t("studentInviteBox.newTenant")
          : ""}
        {[MatchStatusEnum.ARCHIVED].includes(match.match_status!)
          ? t("studentInviteBox.pastTenant")
          : ""}
      </Title>
      {[MatchStatusEnum.CHATTED, MatchStatusEnum.LIKED].includes(
        match.match_status!
      ) ? (
        <>
          <DatesContainer>
            <DateRange
              {...dateIn}
              setIsDateInFirst={setIsDateInFirst}
              isAnyDateIn={isAnyDateOut}
              modeView={"edit"}
              rentalError={rentalError}
              rightBorder={isDateInFirst === 1 ? undefined : dateOut.value}
              isClose
              required
              disabled={
                !(
                  match.match_status === MatchStatusEnum.CHATTED ||
                  match.match_status === MatchStatusEnum.LIKED
                )
              }
            />
            <DateRange
              isDateInFirst={isDateInFirst}
              rentalError={rentalError}
              modeView={"edit"}
              leftBorder={dateIn.value}
              {...dateOut}
              anyEndDate
              isAnyDateOut={isAnyDateOut}
              setIsAnyDateOut={setIsAnyDateOut}
              id="dateOutAv"
              isClose
              required
              disabled={
                !(
                  match.match_status === MatchStatusEnum.CHATTED ||
                  match.match_status === MatchStatusEnum.LIKED
                )
              }
            />
          </DatesContainer>
          {rentPrice}
          <ButtonsContainer>
            <Button
              type="primary"
              style={{ width: "100%" }}
              disabled={!dateIn.value || !dateOut.value}
              loading={sendInvite.isLoading}
              onClick={handleSend}
            >
              {t("modalMessages.invite")}
            </Button>
          </ButtonsContainer>
        </>
      ) : null}
      {[MatchStatusEnum.INVITED].includes(match.match_status!) ? (
        <>
          {rentDates}
          {rentPrice}
          <ButtonsContainer>
            <Button
              type="default"
              onClick={() => {
                handleCancel({
                  host_id: match.host_id,
                  student_id: match.student_id,
                  house_id: match.house_id
                });
              }}
            >
              {t("butCancel")}
            </Button>
            <Button
              type="primary"
              loading={sendInvite.isLoading}
              disabled={allowOnlyCancel}
              onClick={() => {
                setShowEditInvite(true);
              }}
            >
              {t("butEdit")}
            </Button>
          </ButtonsContainer>
        </>
      ) : null}
      {[
        MatchStatusEnum.PENDING,
        MatchStatusEnum.ACTIVE,
        MatchStatusEnum.ACCEPTED
      ].includes(match.match_status!) ? (
        <>
          {rentDates}
          {rentPrice}
          <ButtonsContainer>
            <Button
              type="default"
              onClick={() => {
                handleRemoveTenant({
                  host_id: match.host_id,
                  student_id: match.student_id,
                  house_id: match.house_id
                });
              }}
            >
              {t("butCancel")}
            </Button>
            <Button
              type="primary"
              loading={sendInvite.isLoading}
              disabled={allowOnlyCancel}
              onClick={() => {
                setShowEditInvite(true);
              }}
            >
              {t("butEdit")}
            </Button>
          </ButtonsContainer>
        </>
      ) : null}
      {[MatchStatusEnum.ARCHIVED].includes(match.match_status!) ? (
        <>
          {rentDates}
          {rentPrice}
        </>
      ) : null}
      {showEditInvite ? (
        <SendInviteModal
          isShow={true}
          student={student}
          hostId={host.user_id!}
          houseId={house.house_id!}
          onClose={() => {
            setShowEditInvite(false);
          }}
          onSent={() => {
            setShowEditInvite(false);
          }}
        />
      ) : null}
    </Root>
  );
};
